import type { ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

/*
 * Saved homes live on the visitor's device. The list starts empty on the server
 * and on the first client render, then loads from localStorage after mount.
 */

const STORAGE_KEY = 'viewfinder:saved-homes'

type SavedHomesContextValue = {
    ids: string[]
    ready: boolean
    isSaved: (id: string) => boolean
    toggle: (id: string) => void
}

const SavedHomesContext = createContext<SavedHomesContextValue | null>(null)

const readStorage = (): string[] => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        const parsed: unknown = raw ? JSON.parse(raw) : []

        return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
    }
    catch {
        return []
    }
}

export const SavedHomesProvider = ({ children }: { children: ReactNode }) => {
    const [ids, setIds] = useState<string[]>([])
    const [ready, setReady] = useState(false)

    useEffect(() => {
        setIds(readStorage())
        setReady(true)

        const onStorage = (event: StorageEvent) => {
            if (event.key === STORAGE_KEY) setIds(readStorage())
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    const toggle = useCallback((id: string) => {
        setIds(current => {
            const next = current.includes(id) ? current.filter(item => item !== id) : [id, ...current]

            try {
                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
            }
            catch {
                // Storage can be unavailable in private mode; the list still works for this visit.
            }

            return next
        })
    }, [])

    const value = useMemo<SavedHomesContextValue>(() => ({
        ids,
        ready,
        isSaved: (id: string) => ids.includes(id),
        toggle
    }), [ids, ready, toggle])

    return (
        <SavedHomesContext.Provider value={value}>
            {children}
        </SavedHomesContext.Provider>
    )
}

export const useSavedHomes = (): SavedHomesContextValue => {
    const context = useContext(SavedHomesContext)

    if (!context) throw new Error('useSavedHomes must be used inside SavedHomesProvider.')

    return context
}
